import { useRef, type CSSProperties, type ReactNode } from 'react';
import { C, varColor } from '../builderStyles';

const shared: CSSProperties = {
  margin: 0,
  borderRadius: 10,
  padding: '12px 13px',
  fontSize: 14,
  lineHeight: 1.5,
  fontFamily: 'inherit',
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  boxSizing: 'border-box',
};

interface Props {
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  rows?: number;
}

// Textarea com destaque: o backdrop atrás pinta SÓ o retângulo de fundo de cada
// {{variavel}} (texto invisível); o textarea na frente mostra o texto NORMAL
// (color ink) por cima — assim a seleção nativa e o Ctrl+A funcionam de verdade.
export default function HighlightTextarea({ value, onChange, onBlur, placeholder, rows = 6 }: Props) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const bdRef = useRef<HTMLDivElement>(null);

  const sync = () => {
    if (bdRef.current && taRef.current) {
      bdRef.current.scrollTop = taRef.current.scrollTop;
      bdRef.current.scrollLeft = taRef.current.scrollLeft;
    }
  };

  const nodes: ReactNode[] = [];
  const re = /(\{\{\s*[a-zA-Z0-9_]+\s*\}\})/g;
  value.split(re).forEach((part, i) => {
    const m = /^\{\{\s*([a-zA-Z0-9_]+)\s*\}\}$/.exec(part);
    if (m) {
      const c = varColor(m[1]);
      // só o retângulo de fundo — o texto vem (visível) do textarea na frente.
      nodes.push(
        <span key={i} style={{ background: c.bg, color: 'transparent', borderRadius: 4, fontWeight: 600 }}>{part}</span>,
      );
    } else if (part) {
      nodes.push(<span key={i}>{part}</span>);
    }
  });

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={bdRef}
        aria-hidden
        style={{ ...shared, position: 'absolute', inset: 0, border: '1px solid transparent', background: C.surfaceSoft, color: 'transparent', overflow: 'hidden', pointerEvents: 'none' }}
      >
        {nodes}
        {value.endsWith('\n') ? '​' : ''}
      </div>
      <textarea
        ref={taRef}
        value={value}
        rows={rows}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onScroll={sync}
        onBlur={onBlur}
        spellCheck={false}
        style={{ ...shared, position: 'relative', width: '100%', border: '1px solid ' + C.field, background: 'transparent', color: C.ink, caretColor: C.ink, resize: 'vertical', outline: 'none', display: 'block' }}
      />
    </div>
  );
}
